'use client';

import { faCalendarDays, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { useEffect, useState } from 'react';
import { tv } from 'tailwind-variants';
import { useStore } from 'zustand';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Laboratory, Partner, Professional } from '@medprev-web-app/api-graphql-client';

import { config } from '@/config/config';
import { ProfessionalRedirectModal } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_components/professional-redirect-modal.component';
import { ButtonAddToFavorites } from '@/app/(main)/(pages)/(institucional)/_components/button-add-to-favorites.component';
import { ShareButton } from '@/app/(main)/(pages)/(institucional)/_components/share-button.component';
import { BranchLaboratoryRedirectModal } from '@/app/(main)/(pages)/laboratorios/_components/branch-laboratory-redirect-modal.component';
import { PartnerRedirectModal } from '@/app/(main)/(pages)/parceiros/_components/partner-redirect-modal.component';
import { Branch } from '@/app/(main)/(pages)/_types/branch.type';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { useToast } from '@/components/ui/use-toast';
import { keyName as favoriteKeyName, useFavorite } from '@/hooks/use-favorite.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { useModal } from '@/hooks/use-modal.hook';
import { authStore } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { generateWhatsAppUrl } from '@/utils/formatters/phonenumber';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const buttonMobileStyles = tv({
  slots: {
    buttonMobile: [
      'w-16 h-16 rounded-full bg-green-500',
      'flex items-center justify-center text-white mb-2',
      'hover:bg-green-600 cursor-pointer',
    ],
  },
});

export function ButtonMobile({
  className,
  icon,
  onClick,
  iconSize = 'xl',
  dataTestid = '',
  'data-analytics-event': dataAnalyticsEvent,
}: {
  className?: string;
  icon: IconDefinition;
  onClick?: () => void;
  iconSize?: SizeProp;
  dataTestid?: string;
  ['data-analytics-event']?: string;
}) {
  const { buttonMobile } = buttonMobileStyles();

  return (
    <div data-testid={dataTestid} className={buttonMobile({ className })} onClick={onClick} data-analytics-event={dataAnalyticsEvent}>
      <Icon icon={icon} size={iconSize} />
    </div>
  );
}

interface EntityActionHeaderProps {
  entityType: EntityTypeEnum;
  entity: Professional | Partner | Laboratory | Branch;
  whatsappMessage?: string;
}

export function EntityActionHeader({ entityType, entity, whatsappMessage = '' }: EntityActionHeaderProps) {
  const { toast } = useToast();
  const { open: openRedirect, onOpenChange: onOpenRedirect } = useModal();

  const handleOpenRedirectModal = () => onOpenRedirect(true);

  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const [favorite, setFavorite] = useState(false);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { addFavorite, removeFavorite, checkFavoriteStatus } = useFavorite();
  const { open: openBranchLaboratoryModal, onOpenChange: onOpenBranchLaboratoryModal } = useModal();
  const { open: openPartnerModal, onOpenChange: onOpenPartnerModal } = useModal();

  const enabledRedirect = config.stageOneEnabled;

  const waitForLogin = () =>
    new Promise<void>((resolve, reject) => {
      const unsubscribe = authStore.subscribe((state) => {
        const { authenticatedUser } = state;
        if (authenticatedUser) {
          resolve();
          unsubscribe();
          window.removeEventListener('modalClose', handleClose);
        }
      });

      const handleClose = () => {
        reject();
        unsubscribe();
        toast.warn(`Você precisa estar logado para favoritar um ${favoriteKeyName[entityType]}.`);
        window.removeEventListener('modalClose', handleClose);
      };
      window.addEventListener('modalClose', handleClose);
    });

  useEffect(() => {
    async function fetchFavoriteStatus() {
      const isMarkedAsFavorite = await checkFavoriteStatus({ type: entityType, id: entity?.id as string });
      setFavorite(isMarkedAsFavorite ?? false);
    }
    if (isLoggedIn) {
      fetchFavoriteStatus();
    }
  }, [isLoggedIn, entityType, entity?.id, checkFavoriteStatus]);

  const handleAddToFavorites = async (): Promise<void> => {
    if (!isLoggedIn) {
      setOpenSignInModal(true);
      try {
        await waitForLogin();
      } catch {
        return;
      }

      const isMarkedAsFavorite = await checkFavoriteStatus({ type: entityType, id: entity.id as string });
      setFavorite(isMarkedAsFavorite ?? false);
    }

    try {
      startLoading();
      if (favorite) {
        await removeFavorite({ type: entityType, id: entity.id as string });
        setFavorite(false);
      } else {
        await addFavorite({ type: entityType, id: entity.id as string });
        setFavorite(true);
      }
    } finally {
      stopLoading();
    }
  };

  const handleBranchLaboratoryRedirect = (): void => {
    onOpenBranchLaboratoryModal(true);
  };

  const handlePartnerRedirect = (): void => {
    onOpenPartnerModal(true);
  };

  return (
    <>
      <div>
        <div className="hidden xl:flex items-center gap-3" data-testid="entity-xl-action--action-header">
          {!enabledRedirect && (
            <ButtonAddToFavorites
              favorite={favorite}
              handleAddToFavorites={handleAddToFavorites}
              disabled={isLoading}
            />
          )}
          <ShareButton entityType={entityType} entity={entity} />
        </div>

        <div
          className="hidden xsm:flex xl:hidden items-center justify-between gap-3 w-full"
          data-testid="entity-md-action--action-header"
        >
          {entityType === EntityTypeEnum.PROFESSIONAL && (
            <Button
              startIcon={faCalendarDays}
              className="w-full"
              data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_SCHEDULE_BUTTON_HEADER}
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
              onClick={handleOpenRedirectModal}
            >
              Consultas e horários
            </Button>
          )}

          {entityType === EntityTypeEnum.CLINIC && (
            <Button
              startIcon={faCalendarDays}
              className="w-full"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
              onClick={handlePartnerRedirect}
            >
              Serviços e horários
            </Button>
          )}

          {entityType === EntityTypeEnum.LABORATORY_COLLECT_POINT && (
            <Button
              startIcon={faCalendarDays}
              className="w-full"
              data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_SELECT_REDIRECT_EXAMS_AND_SCHEDULES}
              onClick={handleBranchLaboratoryRedirect}
            >
              Exames e horários
            </Button>
          )}

          {entityType === EntityTypeEnum.BRANCH && 'contacts' in entity && (
            <Button
              data-testid={DataTestIdEnum.BRANCH_SCHEDULE_WHATSAPP_BUTTON}
              startIcon={faWhatsapp}
              className="w-full"
              onClick={() =>
                window.open(
                  generateWhatsAppUrl(entity?.contacts?.whatsapp, whatsappMessage),
                  '_blank',
                  'noopener,noreferrer',
                )}
            >
              Quero agendar
            </Button>
          )}

          <div className="flex items-center gap-3">
            {!enabledRedirect && (
              <ButtonAddToFavorites
                favorite={favorite}
                handleAddToFavorites={handleAddToFavorites}
                disabled={isLoading}
              />
            )}
            <ShareButton entityType={entityType} entity={entity} />
          </div>
        </div>

        <div className="grid grid-cols-3 xsm:hidden w-full" data-testid={DataTestIdEnum.ENTITY_SM_ACTION_HEADER}>
          <div className="flex flex-col items-center">
            {entityType === EntityTypeEnum.PROFESSIONAL && (
              <>
                <ButtonMobile
                  icon={faCalendarDays}
                  onClick={handleOpenRedirectModal}
                  data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
                />
                <span className="text-green-500 font-bold text-center">
                  Consultas e
                  <br />
                  {' '}
                  horários
                </span>
              </>
            )}

            {entityType === EntityTypeEnum.CLINIC && (
              <>
                <ButtonMobile
                  icon={faCalendarDays}
                  onClick={handlePartnerRedirect}
                  data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
                />
                <span className="text-green-500 font-bold text-center">
                  Serviços e
                  <br />
                  {' '}
                  horários
                </span>
              </>
            )}

            {entityType === EntityTypeEnum.LABORATORY_COLLECT_POINT && (
              <>
                <ButtonMobile
                  icon={faCalendarDays}
                  onClick={handleBranchLaboratoryRedirect}
                  data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_SELECT_REDIRECT_EXAMS_AND_SCHEDULES}
                />
                <span className="text-green-500 font-bold text-center">
                  Exames e
                  <br />
                  {' '}
                  horários
                </span>
              </>
            )}

            {entityType === EntityTypeEnum.BRANCH && 'contacts' in entity && (
              <>
                <ButtonMobile
                  dataTestid={DataTestIdEnum.BRANCH_SCHEDULE_WHATSAPP_BUTTON}
                  icon={faWhatsapp}
                  onClick={() =>
                    window.open(
                      generateWhatsAppUrl(entity?.contacts?.whatsapp, whatsappMessage),
                      '_blank',
                      'noopener,noreferrer',
                    )}
                />
                <span className="text-green-500 font-bold text-center">
                  Quero
                  <br />
                  {' '}
                  agendar
                </span>
              </>
            )}
          </div>

          {!enabledRedirect && (
            <div className="flex flex-col items-center">
              <ButtonAddToFavorites
                favorite={favorite}
                handleAddToFavorites={handleAddToFavorites}
                disabled={isLoading}
              />
            </div>
          )}

          <div>
            <ShareButton entityType={entityType} entity={entity} />
          </div>
        </div>
      </div>

      <BranchLaboratoryRedirectModal
        open={openBranchLaboratoryModal}
        onOpenChange={onOpenBranchLaboratoryModal}
        laboratory={entity as Laboratory}
      />

      <PartnerRedirectModal
        open={openPartnerModal}
        onOpenChange={onOpenPartnerModal}
        partner={entity as Partner}
      />

      {openRedirect && (
        <ProfessionalRedirectModal
          open={openRedirect}
          onOpenChange={onOpenRedirect}
          professional={entity as Professional}
        />
      )}
    </>
  );
}
